import type { Object3D } from 'three/webgpu';
import type { GlbHierarchyNode, GlbHierarchyNodeMeta } from '../../imported-model/loaders/glb';
import {
  ImportedModelObjectMetadataService,
} from '../../imported-model/metadata';
import type { ImportedModelObjectHierarchyMetadata } from '../../imported-model/metadata';
import {
  ASSEMBLY_COMPONENT_FIXED_TREE_PREFIX,
  ASSEMBLY_COMPONENT_UNDER_DEFINED_TREE_PREFIX,
  AssemblyComponentFixationState,
  DEFAULT_INSERT_COMPONENT_FIXATION_MODE,
  InsertComponentFixationMode,
} from './AssemblyComponentFixationTypes';
import { collectAssemblyNodeIdentityLookupIds } from '../utils';

/**
 * 插入零部件固定状态解析输入。
 */
export interface ResolveInsertionFixationStateInput {
  /** 插入时选择的固定/浮动策略。 */
  mode?: InsertComponentFixationMode;
  /** 当前装配体是否已经存在可作为零部件的子节点。 */
  hasExistingComponent: boolean;
}

/**
 * 构建插入后层级节点的输入。
 */
export interface BuildInsertedComponentHierarchyNodeInput {
  /** 插入源模型的权威层级根节点。 */
  sourceNode: GlbHierarchyNode;
  /** 插入后在当前装配体内展示的唯一名称。 */
  displayName: string;
  /** 插入后零部件的固定状态。 */
  fixationState: AssemblyComponentFixationState;
  /** 当前装配体根节点 ID。 */
  parentObjectId?: string | null;
  /** 当前装配体根节点路径。 */
  parentPath?: string;
}

/**
 * 层级节点固定状态更新结果。
 */
export interface ApplyHierarchyFixationStateResult {
  /** 更新后的层级根节点。 */
  hierarchy: GlbHierarchyNode;
  /** 是否命中了目标节点并完成更新。 */
  changed: boolean;
}

/**
 * 设计树 label 格式化输入。
 */
export interface FormatComponentTreeLabelInput {
  /** 零部件原始显示名称。 */
  label: string;
  /** 零部件固定状态。 */
  fixationState: AssemblyComponentFixationState;
  /** 浮动零部件是否仍处于欠定义状态。 */
  isUnderDefined?: boolean;
}

/**
 * 已知 SolidWorks 状态前缀集合。
 */
const KNOWN_TREE_STATUS_PREFIX_PATTERN = /^\((?:固定|f|-|\+|\?)\)\s*/i;

/**
 * 克隆 GLB hierarchy metadata。
 *
 * @param meta 原始 metadata。
 * @returns 克隆后的 metadata。
 */
function cloneHierarchyMeta(meta: GlbHierarchyNodeMeta | undefined): GlbHierarchyNodeMeta {
  return {
    hasGeometry: Boolean(meta?.hasGeometry),
    hasDirectGeometry: Boolean(meta?.hasDirectGeometry),
    ...meta,
    materialNames: [...(meta?.materialNames ?? [])],
    bodyEntries: meta?.bodyEntries ? [...meta.bodyEntries] : undefined,
    bodyPrimitiveIndicesById: meta?.bodyPrimitiveIndicesById
      ? { ...meta.bodyPrimitiveIndicesById }
      : undefined,
    bodyHighlightTargetsById: meta?.bodyHighlightTargetsById
      ? { ...meta.bodyHighlightTargetsById }
      : undefined,
    highlightTargets: meta?.highlightTargets ? [...meta.highlightTargets] : undefined,
  };
}

/**
 * 装配体零部件固定/浮动服务。
 *
 * 设计说明：
 * 1. 本类不保存内部状态，所有方法都以输入快照推导输出快照，便于测试和复用；
 * 2. 插入策略、设计树前缀、右键状态切换都统一走这里，避免 UI 与 loader 各自实现；
 * 3. Three.js 对象固定/浮动状态只读写 `Object3D.metadata.importedModel.hierarchy.meta.isFixed`，
 *    禁止再引入 Mate 文档或独立 metadata 键作为第二来源。
 */
export class AssemblyComponentFixationService {
  /**
   * 根据 SolidWorks 插入策略解析本次插入后的固定状态。
   *
   * @param input 解析输入。
   * @returns 本次插入零部件的固定状态。
   */
  public static resolveInsertionFixationState(
    input: ResolveInsertionFixationStateInput,
  ): AssemblyComponentFixationState {
    const mode = input.mode ?? DEFAULT_INSERT_COMPONENT_FIXATION_MODE;
    if (mode === InsertComponentFixationMode.Fixed) {
      return AssemblyComponentFixationState.Fixed;
    }
    if (mode === InsertComponentFixationMode.Floating) {
      return AssemblyComponentFixationState.Floating;
    }
    return input.hasExistingComponent
      ? AssemblyComponentFixationState.Floating
      : AssemblyComponentFixationState.Fixed;
  }

  /**
   * 判断层级节点是否代表一个可固定/浮动的装配体零部件。
   *
   * @param node 目标层级节点。
   * @returns 是否可作为 Fix/Float 命令目标。
   */
  public static isFixableComponentNode(node: GlbHierarchyNode | null | undefined): boolean {
    if (!node) {
      return false;
    }
    const semanticKind = node.meta?.semanticKind;
    return semanticKind === 'assembly'
      || semanticKind === 'part'
      || Boolean(node.meta?.externalFileUuid)
      || Boolean(node.meta?.hasGeometry);
  }

  /**
   * 判断装配体根节点下是否已有零部件。
   *
   * @param hierarchy 当前装配体根层级。
   * @returns 是否存在可固定/浮动子零部件。
   */
  public static hasExistingComponent(hierarchy: GlbHierarchyNode): boolean {
    return hierarchy.children.some((child) => this.isFixableComponentNode(child));
  }

  /**
   * 读取 hierarchy 节点的固定状态。
   *
   * @param node 目标层级节点。
   * @returns 固定时返回 Fixed，否则返回 Floating。
   */
  public static resolveHierarchyNodeFixationState(
    node: GlbHierarchyNode,
  ): AssemblyComponentFixationState {
    return node.meta?.isFixed === true
      ? AssemblyComponentFixationState.Fixed
      : AssemblyComponentFixationState.Floating;
  }

  /**
   * 构建插入到当前装配体后的零部件层级节点。
   *
   * @param input 构建输入。
   * @returns 带唯一名称、父级关系与固定状态的新层级节点。
   */
  public static buildInsertedComponentHierarchyNode(
    input: BuildInsertedComponentHierarchyNodeInput,
  ): GlbHierarchyNode {
    const parentPath = input.parentPath?.trim() ?? '';
    const path = parentPath.length > 0
      ? `${parentPath}/${input.displayName}`
      : input.displayName;
    const depth = path.length > 0 ? path.split('/').length - 1 : 0;
    return this.rebaseInsertedHierarchyNode({
      node: this.cloneHierarchyNode(input.sourceNode),
      name: input.displayName,
      parentObjectId: input.parentObjectId ?? input.sourceNode.parentObjectId,
      path,
      depth,
      fixationState: input.fixationState,
      isRoot: true,
    });
  }

  /**
   * 对 hierarchy 中的指定节点应用固定/浮动状态。
   *
   * @param hierarchy 当前层级根节点。
   * @param objectId 目标节点对象 ID 或选择代理 ID。
   * @param fixationState 目标固定状态。
   * @returns 更新结果；未命中时 `changed=false`。
   */
  public static applyHierarchyNodeFixationState(
    hierarchy: GlbHierarchyNode,
    objectId: string,
    fixationState: AssemblyComponentFixationState,
  ): ApplyHierarchyFixationStateResult {
    const result = this.projectHierarchyNodeFixationState(hierarchy, objectId, fixationState);
    // 旧 API 的 changed 表示“匹配成功”，保留其契约；增量消费者应使用明确的 changedNodes。
    return { hierarchy: result.hierarchy, changed: result.matched };
  }

  /**
   * 将固定状态投影到层级树，仅复制真正变化的节点和祖先路径。
   *
   * 组件纯位姿提交也会携带固定字段；相同字段不能制造新的层级根或触发 UI 整树重建。
   * @param hierarchy 当前层级根。
   * @param objectId 节点或选择代理身份。
   * @param fixationState 目标固定状态。
   * @returns 匹配结果、下一层级与实际变化节点。
   */
  public static projectHierarchyNodeFixationState(
    hierarchy: GlbHierarchyNode,
    objectId: string,
    fixationState: AssemblyComponentFixationState,
  ): PuppyCAD.AssemblyHierarchyFixationProjection {
    const targetIds = new Set(collectAssemblyNodeIdentityLookupIds(objectId));
    const changedNodes: GlbHierarchyNode[] = [];
    let matched = false;
    const isFixed = fixationState === AssemblyComponentFixationState.Fixed;
    if (targetIds.size === 0) return { hierarchy, matched, changedNodes };
    /** @param node 本次访问的层级节点。 @returns 原节点或实际变化路径的新节点。 */
    const visit = (node: GlbHierarchyNode): GlbHierarchyNode => {
      const matches = this.collectHierarchyNodeIdentityLookupIds(node).some(nodeId => targetIds.has(nodeId));
      if (matches) {
        matched = true;
        if (node.meta?.isFixed === isFixed) return node;
        const updated = { ...node, meta: { ...cloneHierarchyMeta(node.meta), isFixed } };
        changedNodes.push(updated);
        return updated;
      }
      // 未发生变化的分支连 children 数组也不分配，避免一次组件位姿同步复制全树中间数组。
      let children: GlbHierarchyNode[] | null = null;
      for (let index = 0; index < node.children.length; index++) {
        const previous = node.children[index];
        const next = visit(previous);
        if (next !== previous) {
          children ??= node.children.slice();
          children[index] = next;
        }
      }
      return children ? { ...node, children } : node;
    };
    const nextHierarchy = visit(hierarchy);
    return { hierarchy: nextHierarchy, matched, changedNodes };
  }

  /**
   * 格式化零部件在 FeatureManager 设计树中的显示名称。
   *
   * @param input 格式化输入。
   * @returns 带 SolidWorks 状态前缀的 label。
   */
  public static formatComponentTreeLabel(input: FormatComponentTreeLabelInput): string {
    const normalizedLabel = this.trimComponentTreeStatusPrefix(input.label);
    if (input.fixationState === AssemblyComponentFixationState.Fixed) {
      return `${ASSEMBLY_COMPONENT_FIXED_TREE_PREFIX} ${normalizedLabel}`;
    }
    if (input.isUnderDefined ?? true) {
      return `${ASSEMBLY_COMPONENT_UNDER_DEFINED_TREE_PREFIX} ${normalizedLabel}`;
    }
    return normalizedLabel;
  }

  /**
   * 去除已知设计树状态前缀。
   *
   * @param label 原始 label。
   * @returns 不含状态前缀的 label。
   */
  public static trimComponentTreeStatusPrefix(label: string): string {
    return label.replace(KNOWN_TREE_STATUS_PREFIX_PATTERN, '').trim();
  }

  /**
   * 判断 Three.js 对象是否属于固定零部件。
   *
   * @param object 场景对象。
   * @returns 固定状态；找不到固定信息时按浮动处理。
   */
  public static isSceneObjectFixed(object: Object3D | null | undefined): boolean {
    return this.resolveSceneObjectFixationState(object) === AssemblyComponentFixationState.Fixed;
  }

  //------ 装配体零部件固定状态变换准入 开始------
  /**
   * 判断 Three.js 对象链上是否存在明确的装配体固定/浮动状态。
   *
   * @param object 场景对象。
   * @returns 对象自身或父级链路存在 `metadata.importedModel.hierarchy.meta.isFixed` 布尔值时返回 `true`。
   */
  public static hasSceneObjectFixationState(object: Object3D | null | undefined): boolean {
    let current: Object3D | null | undefined = object;
    while (current) {
      const isFixed = this.resolveSceneObjectFixationMetadata(current);
      if (typeof isFixed === 'boolean') {
        return true;
      }
      current = current.parent;
    }
    return false;
  }

  /**
   * 判断 Three.js 对象是否允许被装配体移动/旋转命令改变位姿。
   *
   * 设计说明：
   * 1. SolidWorks 中只有明确固定的零部件才禁止移动或旋转；
   * 2. 缺少 `metadata.importedModel.hierarchy.meta.isFixed` 的对象按浮动处理，避免未纳入装配语义的对象被错误锁定；
   * 3. 所有移动/旋转入口都应调用本方法，避免不同交互路径各自判断导致行为不一致。
   *
   * @param object 场景对象。
   * @returns 对象存在且未被明确固定时返回 `true`。
   */
  public static canTransformSceneObject(object: Object3D | null | undefined): boolean {
    return Boolean(object) && !this.isSceneObjectFixed(object);
  }
  //------ 装配体零部件固定状态变换准入 结束------

  /**
   * 读取 Three.js 对象链上的零部件固定状态。
   *
   * @param object 场景对象。
   * @returns 固定状态。
   */
  public static resolveSceneObjectFixationState(
    object: Object3D | null | undefined,
  ): AssemblyComponentFixationState {
    let current: Object3D | null | undefined = object;
    while (current) {
      const isFixed = this.resolveSceneObjectFixationMetadata(current);
      if (typeof isFixed === 'boolean') {
        return isFixed
          ? AssemblyComponentFixationState.Fixed
          : AssemblyComponentFixationState.Floating;
      }
      current = current.parent;
    }
    return AssemblyComponentFixationState.Floating;
  }

  /**
   * 将固定状态写回 Three.js 对象链上的 imported-model hierarchy meta。
   *
   * @param object 场景对象。
   * @param fixationState 固定状态。
   */
  public static writeSceneObjectFixationState(
    object: Object3D | null | undefined,
    fixationState: AssemblyComponentFixationState,
  ): void {
    if (!object) {
      return;
    }
    const isFixed = fixationState === AssemblyComponentFixationState.Fixed;
    object.traverse((child) => {
      this.writeSceneObjectFixationMetadata(child, isFixed, {
        allowCreateFallbackHierarchy: child === object,
      });
    });
  }

  /**
   * 克隆 GLB hierarchy 节点。
   *
   * @param node 原始节点。
   * @returns 深克隆节点。
   */
  private static cloneHierarchyNode(node: GlbHierarchyNode): GlbHierarchyNode {
    return {
      ...node,
      meta: cloneHierarchyMeta(node.meta),
      children: node.children.map((child) => this.cloneHierarchyNode(child)),
    };
  }

  /**
   * 将插入源 hierarchy 重新挂接到当前装配体路径下。
   *
   * @param input 挂接输入。
   * @returns 路径、深度与父级关系已重算的 hierarchy 节点。
   */
  private static rebaseInsertedHierarchyNode(input: {
    /** 当前正在挂接的节点。 */
    node: GlbHierarchyNode;
    /** 当前节点显示名称。 */
    name: string;
    /** 当前节点父级 objectId。 */
    parentObjectId: string | null;
    /** 当前节点装配体路径。 */
    path: string;
    /** 当前节点深度。 */
    depth: number;
    /** 插入根节点的固定状态。 */
    fixationState: AssemblyComponentFixationState;
    /** 是否为本次插入的根节点。 */
    isRoot: boolean;
  }): GlbHierarchyNode {
    const meta = cloneHierarchyMeta(input.node.meta);
    const currentNode: GlbHierarchyNode = {
      ...input.node,
      name: input.name,
      parentObjectId: input.parentObjectId,
      path: input.path,
      depth: input.depth,
      meta: input.isRoot
        ? {
            ...meta,
            isFixed: input.fixationState === AssemblyComponentFixationState.Fixed,
          }
        : meta,
      children: [],
    };
    const children = input.node.children.map((child) => {
      const childName = child.name?.trim() || child.objectId;
      const childPath = input.path.length > 0 ? `${input.path}/${childName}` : childName;
      // 子节点保留源文件内部的固定/浮动语义，只重算装配体路径和父级关系。
      return this.rebaseInsertedHierarchyNode({
        node: child,
        name: child.name,
        parentObjectId: input.node.objectId,
        path: childPath,
        depth: input.depth + 1,
        fixationState: input.fixationState,
        isRoot: false,
      });
    });
    return {
      ...currentNode,
      children,
    };
  }

  /**
   * 收集 hierarchy 节点可用于固定状态命中的稳定身份。
   *
   * @param node hierarchy 节点。
   * @returns 节点自身和语义元数据派生出的 lookup ID。
   */
  private static collectHierarchyNodeIdentityLookupIds(node: GlbHierarchyNode): string[] {
    return [
      node.objectId,
      node.path,
      node.meta?.selectionObjectId,
      node.meta?.assemblyNodeId,
    ]
      .flatMap((value) => collectAssemblyNodeIdentityLookupIds(value))
      .filter((value, index, array) => array.indexOf(value) === index);
  }

  /**
   * 从 Object3D 的 imported-model hierarchy meta 中读取固定/浮动状态。
   *
   * @param object 场景对象。
   * @returns 读取到布尔值时返回固定状态，否则返回 `undefined`。
   */
  private static resolveSceneObjectFixationMetadata(object: Object3D): boolean | undefined {
    const isFixed = ImportedModelObjectMetadataService.resolveHierarchy(object)?.meta?.isFixed;
    return typeof isFixed === 'boolean' ? isFixed : undefined;
  }

  /**
   * 为显式写入固定状态的运行时对象创建最小 hierarchy metadata。
   *
   * @param object 场景对象。
   * @param isFixed 是否固定。
   * @returns 可写入 imported-model metadata 的 hierarchy 镜像。
   */
  private static createFallbackFixationHierarchyMetadata(
    object: Object3D,
    isFixed: boolean,
  ): ImportedModelObjectHierarchyMetadata {
    const objectName = object.name.trim() || object.uuid;
    return {
      fileId: object.uuid,
      name: objectName,
      path: objectName,
      nodeType: object.type,
      selectable: true,
      parentObjectId: object.parent?.uuid ?? null,
      depth: 0,
      meta: {
        ...cloneHierarchyMeta(undefined),
        assemblyNodeId: object.uuid,
        selectionObjectId: object.uuid,
        isFixed,
      },
    };
  }

  /**
   * 将固定/浮动状态写入 Object3D 的 imported-model hierarchy meta。
   *
   * @param object 场景对象。
   * @param isFixed 是否固定。
   */
  private static writeSceneObjectFixationMetadata(
    object: Object3D,
    isFixed: boolean,
    options: { readonly allowCreateFallbackHierarchy: boolean },
  ): void {
    const hierarchy = ImportedModelObjectMetadataService.peekMetadata(object)?.hierarchy
      ?? (
        options.allowCreateFallbackHierarchy
          ? this.createFallbackFixationHierarchyMetadata(object, isFixed)
          : undefined
      );
    if (!hierarchy) {
      return;
    }

    ImportedModelObjectMetadataService.writeMetadata(object, {
      hierarchy: {
        ...hierarchy,
        meta: {
          ...cloneHierarchyMeta(hierarchy.meta),
          isFixed,
        },
      },
    });
  }

}
